'use client';

import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Pagination } from 'swiper/modules';
import Header from '@/components/Header';
import HeroImage from '@/components/HeroImage';
import Footer from '@/components/Footer';
import { teamMembers } from '@/lib/data/team';
import styles from './why-apex.module.css';
import { BreadcrumbSchema } from '@/lib/schema';

import 'swiper/css';
import 'swiper/css/pagination';


export default function WhyApexPage() {
  const [imageLoaded, setImageLoaded] = useState<Record<string, boolean>>({});
  const t = useTranslations('whyApex');
  const tCommon = useTranslations('common');
  const swiperRef = useRef<SwiperType | null>(null);

  // Get differentiators from translations
  const differentiators = [
    {
      id: 1,
      title: t('differentiators.customized.title'),
      description: t('differentiators.customized.description'),
      features: [
        t('differentiators.customized.features.0'),
        t('differentiators.customized.features.1'),
        t('differentiators.customized.features.2'),
        t('differentiators.customized.features.3'),
      ],
    },
    {
      id: 2,
      title: t('differentiators.guides.title'),
      description: t('differentiators.guides.description'),
      features: [
        t('differentiators.guides.features.0'),
        t('differentiators.guides.features.1'),
        t('differentiators.guides.features.2'),
        t('differentiators.guides.features.3'),
      ],
    },
    {
      id: 3,
      title: t('differentiators.access.title'),
      description: t('differentiators.access.description'),
      features: [
        t('differentiators.access.features.0'),
        t('differentiators.access.features.1'),
        t('differentiators.access.features.2'),
        t('differentiators.access.features.3'),
      ],
    },
    {
      id: 4,
      title: t('differentiators.equipment.title'),
      description: t('differentiators.equipment.description'),
      features: [
        t('differentiators.equipment.features.0'),
        t('differentiators.equipment.features.1'),
        t('differentiators.equipment.features.2'),
        t('differentiators.equipment.features.3'),
      ],
    },
    {
      id: 5,
      title: t('differentiators.commitment.title'),
      description: t('differentiators.commitment.description'),
      features: [
        t('differentiators.commitment.features.0'),
        t('differentiators.commitment.features.1'),
        t('differentiators.commitment.features.2'),
        t('differentiators.commitment.features.3'),
      ],
    },
    {
      id: 6,
      title: t('differentiators.luxury.title'),
      description: t('differentiators.luxury.description'),
      features: [
        t('differentiators.luxury.features.0'),
        t('differentiators.luxury.features.1'),
        t('differentiators.luxury.features.2'),
        t('differentiators.luxury.features.3'),
      ],
    },
  ];

  // Get stats from translations
  const stats = [
    { id: 'satisfaction', number: '98%', label: t('stats.satisfaction') },
    { id: 'repeat', number: '85%', label: t('stats.repeat') },
    { id: 'experience', number: '10+', label: t('stats.experience') },
    { id: 'safety', number: '100%', label: t('stats.safety') },
  ];

  return (
    <div className={styles.pageWrapper}>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: '/' },
        { name: tCommon('whyApex'), href: '/why-apex/' },
      ]} />
      <Header />

      <HeroImage
        backgroundImage="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80"
        title={t('hero.title')}
        subtitle={t('hero.subtitle')}
        location={t('hero.location')}
      />

      <main>
        {/* Introduction Section */}
        <motion.section
          className={styles.introSection}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className={styles.container}>
            <div className={styles.introContent}>
              <h1>{t('intro.title')}</h1>
              <p>{t('intro.description')}</p>
            </div>
          </div>
        </motion.section>

        {/* Differentiators Section */}
        <section className={styles.differentiatorsSection}>
          <div className={styles.container}>
            <div className={styles.differentiatorsGrid}>
              {differentiators.map((diff, index) => (
                <motion.article
                  key={diff.id}
                  className={styles.diffCard}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className={styles.diffNumber}>
                    {String(diff.id).padStart(2, '0')}
                  </div>
                  <div className={styles.diffContent}>
                    <h3>{diff.title}</h3>
                    <p>{diff.description}</p>
                    <ul className={styles.diffList}>
                      {diff.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Guarantee Section */}
        <motion.section
          className={styles.guaranteeSection}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className={styles.container}>
            <div className={styles.guaranteeContent}>
              <h2>{t('guarantee.title')}</h2>
              <p className={styles.guaranteeIntro}>{t('guarantee.intro')}</p>
              <div className={styles.guaranteeBox}>
                <p className={styles.guaranteeText}>{t('guarantee.text')}</p>
              </div>

              {/* Stats Grid */}
              <div className={styles.statsGrid}>
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.id}
                    className={styles.statItem}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className={styles.statNumber}>{stat.number}</div>
                    <p className={styles.statLabel}>{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Team Carousel Section */}
        <motion.section
          className={styles.teamSection}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className={styles.container}>
            <h2 className={styles.teamSectionTitle}>{t('team.title')}</h2>
            <p className={styles.teamSectionSubtitle}>{t('team.subtitle')}</p>
          </div>

          <div className={styles.teamCarouselWrapper}>
            <Swiper
              onSwiper={(swiper) => { swiperRef.current = swiper; }}
              modules={[Pagination]}
              slidesPerView={1}
              spaceBetween={24}
              loop={true}
              grabCursor={true}
              pagination={{ clickable: true, dynamicBullets: true }}
              breakpoints={{
                640: {
                  slidesPerView: 2,
                  spaceBetween: 24,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 32,
                },
              }}
              className={styles.teamSwiper}
            >
              {teamMembers.map((member, index) => (
                <SwiperSlide key={member.id}>
                  <article
                    className={styles.teamCard}
                    role="article"
                    aria-label={t('team.memberAriaLabel', { name: member.name, role: member.role })}
                    tabIndex={0}
                  >
                    <div className={styles.teamCardMedia}>
                      {!imageLoaded[member.id] && (
                        <div className={styles.teamCardSkeleton} aria-label={t('team.loadingPhoto')} />
                      )}
                      <Image
                        src={member.image}
                        alt={`${member.name} - ${member.role}`}
                        fill
                        sizes="(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 400px"
                        className={imageLoaded[member.id] ? styles.teamCardImgLoaded : styles.teamCardImgLoading}
                        quality={85}
                        priority={index < 3}
                        onLoad={() => setImageLoaded(prev => ({ ...prev, [member.id]: true }))}
                      />
                    </div>
                    <div className={styles.teamCardInfo}>
                      <h3 className={styles.teamCardName}>{member.name}</h3>
                      <p className={styles.teamCardRole}>{member.role}</p>
                      <p className={styles.teamCardQuote}>
                        &quot;{member.handwrittenNote || member.bio}&quot;
                      </p>
                    </div>
                    <div className={styles.teamCardBio}>
                      <p>{member.story || member.bio}</p>
                    </div>
                  </article>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Navigation OUTSIDE Swiper - static, won't move with card animations */}
            <button
              className={styles.teamNavPrev}
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label={t('team.prevAriaLabel')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              className={styles.teamNavNext}
              onClick={() => swiperRef.current?.slideNext()}
              aria-label={t('team.nextAriaLabel')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </button>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className={styles.ctaSection}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className={styles.container}>
            <div className={styles.ctaContent}>
              <h2>{t('cta.title')}</h2>
              <p>{t('cta.description')}</p>
              <Link href="/contact/" className={styles.ctaButton}>
                {t('cta.button')}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
}
